import { create } from 'zustand';
import { api } from '../services/api';

// Word Binder side panel on D1 (Chunk W10). One read: GET /api/binder.
// Game Words and Social only words, Word Bank-approved only (Related rows under
// Game Words removed 2026-10-22).

export type ContentStatus = 'none' | 'generated' | 'needs_fixes' | 'approved' | 'missing';

export interface BinderWord {
  id: string;
  word: string;
  bucket?: string;
  status: ContentStatus;
  bank_approved?: boolean;
  content_approved_at?: string | null;
  // Stages approved of the word's total (Game 5, Social 3); null once content is approved.
  progress?: { approved: number; total: number } | null;
  // What to do next (2026-10-22): the next stage in order and its status, flags, difficulty.
  guide?: BinderGuide;
  // Game Words only; absent on Social only words.
  game_date?: string | null;
  played_on?: string | null;
  // Pronunciation check (2026-10-25): needs_check = Publish would refuse it; null before the migration.
  pronunciation?: { status: 'auto' | 'approved' | 'needs_check' | null; needs_check: boolean } | null;
}

export interface BinderGuide {
  done: number;
  total: number;
  // Stage id (1, 1b, rv, as, hk, 2, 3), or null when every stage is done.
  next: string | null;
  next_status: 'empty' | 'generated' | 'needs_fixes' | 'approved' | null;
  needs_fixes: boolean;
  started: boolean;
  last_worked_at: string | null;
  difficulty: number | null;
}

export interface BinderGame extends BinderWord {
  game_date: string | null;
  calendar_status: 'draft' | 'approved' | null;
  // The day its game was played, once that day has passed (Central).
  played_on: string | null;
}

export interface BinderSocial extends BinderWord {
  linked_to: string[];
}

interface BinderState {
  game: BinderGame[];
  social: BinderSocial[];
  loading: boolean;
  error: string | null;
  fetchBinder: () => Promise<void>;
}

export const useBinderStore = create<BinderState>()((set) => ({
  game: [],
  social: [],
  loading: false,
  error: null,

  fetchBinder: async () => {
    set({ loading: true, error: null });
    try {
      const data = (await api.get('/api/binder')) as { game?: BinderGame[]; social?: BinderSocial[] };
      set({ game: data.game || [], social: data.social || [], loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },
}));
